import React, { useState, useEffect } from 'react';
import { BookOpen, Globe, Building2, DollarSign, Phone, MapPin, CheckCircle2, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || (axios.defaults.headers.common['Authorization'] || '').replace('Bearer ', '');

const ICON_MAP = {
  'Countries & Cities': Globe,
  'Venues': Building2,
  'Currencies': DollarSign,
  'Telephone Codes': Phone,
  'City-State Countries': MapPin,
  'Data Integrity': CheckCircle2,
};

const CATEGORY_COLORS = {
  'Countries & Cities': 'bg-blue-50 border-blue-200 text-blue-700',
  'Venues': 'bg-purple-50 border-purple-200 text-purple-700',
  'Currencies': 'bg-green-50 border-green-200 text-green-700',
  'Telephone Codes': 'bg-orange-50 border-orange-200 text-orange-700',
  'City-State Countries': 'bg-yellow-50 border-yellow-200 text-yellow-700',
  'Data Integrity': 'bg-red-50 border-red-200 text-red-700',
};

export default function UltraRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/dropdown/rules`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        setRules(res.data.items || []);
      } catch {
        // fallback hardcoded if API fails
        setRules([
          { id: '1', category: 'Countries & Cities', rule: 'All country and city dropdowns in forms must pull data from the Ultra Admin > Dropdown Management > Countries & Cities module. No hardcoded country or city list is allowed anywhere in the application.', applies_to: 'All forms (Signup, Brief Wizard, Profile, etc.)' },
          { id: '2', category: 'Venues', rule: 'All venue/exhibition center dropdowns must pull data from the Ultra Admin > Dropdown Management > Venues module. Venue lists must be filtered by the selected country and city.', applies_to: 'Brief Wizard (Step 1: Event Info), Profile forms' },
          { id: '3', category: 'Currencies', rule: 'All currency dropdowns must pull data from the Ultra Admin > Dropdown Management > Currencies module. No hardcoded currency list (e.g. EUR, USD) is allowed.', applies_to: 'Budget forms, Profile forms, Billing' },
          { id: '4', category: 'Telephone Codes', rule: 'All phone number fields must use a split layout: (1) Country code dropdown from Dropdown Management > Phone Codes module, (2) Number input (digits only, max 12 characters, no letters or special characters).', applies_to: 'Signup forms, Contact forms, Profile forms' },
          { id: '5', category: 'City-State Countries', rule: 'Countries that are city-states (Singapore, Monaco, Vatican City, San Marino, Liechtenstein, Maldives, etc.) must have a single city entry identical to the country name. These are not duplicates — the city IS the state.', applies_to: 'Countries & Cities module' },
          { id: '6', category: 'Data Integrity', rule: 'Never use fake or placeholder data for geographic, currency, or phone code information. All data must match real-world UN-registered country data.', applies_to: 'All dropdown management modules' },
        ]);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center">
              <BookOpen size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Platform Rules</h1>
              <p className="text-gray-500 text-sm">Governing rules for all dropdown data across the platform</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 size={14} className="text-green-600" />
          <span className="text-sm text-green-700 font-medium">{rules.length} Active Rules</span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-gray-400">
          <RefreshCw size={20} className="animate-spin mr-2" /> Loading rules...
        </div>
      ) : (
        <div className="grid gap-4">
          {rules.map((rule, idx) => {
            const Icon = ICON_MAP[rule.category] || BookOpen;
            const colorClass = CATEGORY_COLORS[rule.category] || 'bg-gray-50 border-gray-200 text-gray-700';
            return (
              <div key={rule.id || idx} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-sm transition-shadow">
                <div className="flex items-start gap-4">
                  {/* Number */}
                  <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-gray-500">{idx + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    {/* Category badge */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border ${colorClass}`}>
                        <Icon size={12} />
                        {rule.category}
                      </span>
                    </div>
                    {/* Rule */}
                    <p className="text-gray-800 text-sm leading-relaxed mb-3">{rule.rule}</p>
                    {/* Applies to */}
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <span className="font-medium">Applies to:</span>
                      <span className="bg-gray-100 px-2 py-0.5 rounded">{rule.applies_to}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer note */}
      <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
        <p className="text-xs text-gray-500">
          <span className="font-semibold text-gray-700">Important:</span> These rules are enforced throughout the application. Any new forms or pages must follow these conventions without exception. All dropdown data is managed centrally via the Dropdown Management module.
        </p>
      </div>
    </div>
  );
}
